import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { AlertCircle, Info, X } from "lucide-react";
import React from "react";

// TypeScript Interfaces
export interface PaymentDeferredDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void | Promise<void>;
  loading?: boolean;
  title?: string;
  noticeMessage?: string;
  detailMessage?: string;
  continueButtonText?: string;
  cancelButtonText?: string;
}

export const PaymentDeferredDialog: React.FC<
  PaymentDeferredDialogProps
> = ({
  open,
  onClose,
  onContinue,
  loading = false,
  title = "Payment Deferred",
  noticeMessage = "The application fees will be captured in the customer's first bill.",
  detailMessage = "By choosing to pay later, the customer will be charged the application processing fees along with their first billing cycle. Do you want to continue to the installation step?",
  continueButtonText = "Continue to Installation",
  cancelButtonText = "Cancel",
}) => {
  // Handle continue action
  const handleContinue = async () => {
    try {
      await onContinue();
      onClose();
    } catch (error) {
      console.error("Continue action error:", error);
      // Keep dialog open on error
    }
  };

  // Handle close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {title}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Important Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  Important Notice:
                </h4>
                <p className="text-sm text-blue-800">{noticeMessage}</p>
              </div>
            </div>
          </div>

          {/* Detail Message */}
          <div className="text-sm text-gray-700 leading-relaxed">
            {detailMessage}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-gray-200 bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {cancelButtonText}
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? "Processing..." : continueButtonText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDeferredDialog;